import React, { useState } from "react";
import { WorkspaceMember } from "@common/types/workspace";
import { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useActiveWorkspaceDetails } from "@hooks/useActiveWorkspaceDetails";
import { useEntities } from "@hooks/useEntities";
import { useCanManageWorkspaceAssignment } from "@hooks/permissions/useCanManageWorkspaceAssignment";
import { useActiveWorkspaceRole } from "@hooks/permissions/useActiveWorkspaceRole";
import { useUser } from "@hooks/useUser";
import { useOverlays } from "contexts/OverlayContext";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useRemoveMemberMutation } from "@api/workspaceMember.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { TransferPrimaryAdminModal } from "./TransferPrimaryAdminModal";

interface Props {
    member: WorkspaceMember;
}

export const MemberActions: React.FC<Props> = ({ member }) => {
    const { user, role } = member;

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const canManage = useCanManageWorkspaceAssignment(member);
    const { openOverlay } = useOverlays();
    const activeWorkspace = useActiveWorkspace();
    const actingUserRole = useActiveWorkspaceRole();
    const entities = useEntities({
        requireAdminAccess: true,
        excludeMockEntities: true,
    });
    const workspaceDetails = useActiveWorkspaceDetails();
    const currentUser = useUser();
    const removeMember = useRemoveMemberMutation(member, activeWorkspace!);
    const { error: errorToast } = useToaster();

    const hasOrganization =
        (workspaceDetails?.organizationAssignments.length ?? 0) > 0;
    const currentUserIsPrimaryAdmin =
        activeWorkspace?.primaryAdminUserId === currentUser?.id;
    const targetIsNotCurrentUser = user.id !== currentUser?.id;
    const targetIsAcceptedMember = role !== UserWorkspaceRole.PRIMARY_ADMIN;

    const canTransferPrimaryAdmin =
        currentUserIsPrimaryAdmin &&
        !hasOrganization &&
        targetIsNotCurrentUser &&
        targetIsAcceptedMember;

    if (!canManage && !canTransferPrimaryAdmin) {
        return null;
    }

    return (
        <>
            <Menu.More className={tableStyles.showOnHover}>
                {canManage && (
                    <Menu.Item
                        onClick={() => {
                            openOverlay(
                                "memberManagementConfigurePermissions",
                                {
                                    member,
                                    workspaceId: activeWorkspace?.id,
                                    actingUserRole: actingUserRole ?? undefined,
                                    entities,
                                },
                            );
                        }}
                    >
                        Configure permissions
                    </Menu.Item>
                )}

                {canTransferPrimaryAdmin && (
                    <Menu.Item onClick={() => setShowTransferModal(true)}>
                        Transfer primary admin role
                    </Menu.Item>
                )}

                {canManage && (
                    <Menu.Item
                        destructive
                        onClick={() => setShowDeleteConfirmation(true)}
                    >
                        Remove from workspace
                    </Menu.Item>
                )}
            </Menu.More>

            <ConfirmationModal
                show={showDeleteConfirmation}
                title={`Are you sure you want to remove ${user.name || user.email} from the workspace?`}
                question=""
                onConfirm={async () => {
                    try {
                        await removeMember.mutateAsync();
                        setShowDeleteConfirmation(false);
                    } catch {
                        errorToast("Failed to remove member from workspace");
                    }
                }}
                no="Cancel"
                yes="Remove"
                onReject={() => setShowDeleteConfirmation(false)}
            />

            {canTransferPrimaryAdmin && (
                <TransferPrimaryAdminModal
                    member={member}
                    show={showTransferModal}
                    onHide={() => setShowTransferModal(false)}
                />
            )}
        </>
    );
};
